"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowRight, Globe, Smartphone, Database, CheckCircle2, Zap, Bot, MessageSquare, Users, BarChart3, Terminal } from "lucide-react";
import { DiscoveryCategory } from "./ContextualDiscoveryModal";
import CrmAutomationSimulator from "../interactive/CrmAutomationSimulator";

interface Act2WhatWeBuildProps {
  onOpenDiscovery: (category?: DiscoveryCategory) => void;
}

export const Act2WhatWeBuild: React.FC<Act2WhatWeBuildProps> = ({ onOpenDiscovery }) => {
  return (
    <div id="scene-studios" className="relative w-full text-[#111318]">
      {/* =========================================================================
          SCENE 05: WEB DEVELOPMENT STUDIO — FULL-BLEED WORKSPACE CANVAS
      ========================================================================= */}
      <section
        id="scene-studio-web"
        className="relative min-h-screen w-full flex items-center justify-between px-6 sm:px-12 lg:px-20 py-24 overflow-hidden border-b border-[#111318]/10 bg-slate-900"
      >
        {/* Real High-Resolution Web Engineering Studio Photograph */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/storytelling/office/web-development.webp"
            alt="Johnson Softwares Web Application Engineering Studio"
            fill
            sizes="100vw"
            quality={85}
            loading="lazy"
            className="object-cover object-center"
          />
        </div>

        {/* Localized subtle left gradient behind text */}
        <div className="absolute inset-y-0 left-0 w-full sm:w-[60%] lg:w-[48%] bg-gradient-to-r from-white/92 via-white/75 to-transparent pointer-events-none z-[1]" />

        {/* Narrator Typography Sitting in Natural Left Negative Space */}
        <div className="relative z-10 max-w-xl text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#2563EB]/10 border border-[#2563EB]/20 text-xs font-mono text-[#2563EB] font-semibold mb-4">
            <Globe className="w-3.5 h-3.5" />
            <span>STUDIO 01 // WEB &amp; CLOUD PLATFORMS</span>
          </div>

          <h3 className="text-3xl sm:text-5xl font-extrabold text-[#111318] tracking-tight leading-tight mb-5">
            DIGITAL EXPERIENCES THAT WORK AS HARD AS YOUR BUSINESS.
          </h3>

          <p className="text-base text-[#374151] leading-relaxed mb-6">
            We design and engineer bespoke web platforms, secure customer portals, and high-concurrency cloud systems built on Next.js, React, and isolated PostgreSQL databases. Sub-second load times, instant edge caching, and zero data leakage by default.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-8">
            {[
              "Next.js App Router Architecture",
              "Sub-1.2s Largest Contentful Paint",
              "Self-Hosted or Serverless Cloud",
              "Role-Based Access Control (RBAC)",
              "Full REST & GraphQL API Layers",
              "100% Client Code & IP Ownership",
            ].map((feat) => (
              <div key={feat} className="flex items-center gap-2 text-xs font-medium text-[#111318]">
                <CheckCircle2 className="w-4 h-4 text-[#2563EB] shrink-0" />
                <span>{feat}</span>
              </div>
            ))}
          </div>

          <div>
            <button
              onClick={() => onOpenDiscovery("website")}
              className="px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white text-sm font-semibold transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] flex items-center gap-2"
            >
              <span>BUILD MY WEBSITE</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Physically Connected Workstation Monitor / UI Telemetry in Perspective */}
        <div className="relative z-10 hidden xl:block w-full max-w-sm rounded-2xl bg-white/95 border border-[#111318]/15 shadow-2xl p-6 backdrop-blur-sm self-end mb-8">
          <div className="flex items-center justify-between pb-3 border-b border-[#111318]/10 text-xs font-mono">
            <span className="flex items-center gap-2 text-[#111318] font-bold">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              RUNTIME TELEMETRY
            </span>
            <span className="text-[#2563EB] font-bold">99.98% UPTIME</span>
          </div>

          <div className="pt-4 space-y-2.5 text-xs font-mono">
            <div className="p-2.5 rounded-lg bg-[#F8F8F6] border border-[#111318]/5 flex items-center justify-between">
              <span className="text-[#5E636B]">ENGINE</span>
              <span className="font-bold text-[#111318]">Next.js 16 + React 19</span>
            </div>
            <div className="p-2.5 rounded-lg bg-[#F8F8F6] border border-[#111318]/5 flex items-center justify-between">
              <span className="text-[#5E636B]">PERFORMANCE</span>
              <span className="font-bold text-emerald-600">0.8s LCP (Green CWV)</span>
            </div>
            <div className="p-2.5 rounded-lg bg-[#F8F8F6] border border-[#111318]/5 flex items-center justify-between">
              <span className="text-[#5E636B]">DATABASE</span>
              <span className="font-bold text-[#2563EB]">Isolated PostgreSQL</span>
            </div>
          </div>
        </div>

        {/* Floating Studio Floor Marker */}
        <div className="absolute top-8 right-8 z-10 hidden sm:flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-md text-[11px] font-mono text-[#111318]">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          <span>LEVEL 2 // STUDIO 01 // WEB SYSTEMS</span>
        </div>
      </section>

      {/* =========================================================================
          SCENE 06: MOBILE DEVELOPMENT — FULL-BLEED WORKSPACE CANVAS
      ========================================================================= */}
      <section
        id="scene-studio-mobile"
        className="relative min-h-screen w-full flex items-center justify-end px-6 sm:px-12 lg:px-20 py-24 overflow-hidden border-b border-[#111318]/10 bg-slate-900"
      >
        {/* Real High-Resolution Mobile Engineering Studio Photograph */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/images/storytelling/office/mobile-development.webp"
            alt="Johnson Softwares Mobile Application Development Studio"
            fill
            sizes="100vw"
            quality={85}
            loading="lazy"
            className="object-cover object-center"
          />
        </div>

        {/* Localized subtle right gradient behind text */}
        <div className="absolute inset-y-0 right-0 w-full sm:w-[60%] lg:w-[48%] bg-gradient-to-l from-white/92 via-white/75 to-transparent pointer-events-none z-[1]" />

        {/* Physically Connected Field App Telemetry Card in Perspective (Left) */}
        <div className="relative z-10 hidden xl:block w-full max-w-xs rounded-2xl bg-white/95 border border-[#111318]/15 shadow-2xl p-5 self-end mb-8 mr-auto">
          <div className="flex items-center justify-between text-[11px] font-mono text-[#5E636B] pb-2 border-b border-[#111318]/10">
            <span className="font-bold text-[#111318]">FIELD APP RUNTIME</span>
            <span className="text-emerald-600 font-bold">OFFLINE SYNC ARMED</span>
          </div>
          <div className="pt-3 space-y-2 text-xs">
            <div className="flex justify-between items-center text-[#111318]">
              <span>Biometric Security</span>
              <span className="font-mono text-emerald-600 font-bold">FaceID Ready</span>
            </div>
            <div className="flex justify-between items-center text-[#111318]">
              <span>GPS Telemetry</span>
              <span className="font-mono text-[#2563EB] font-bold">Live Stream</span>
            </div>
            <div className="flex justify-between items-center text-[#111318]">
              <span>WhatsApp Alerts</span>
              <span className="font-mono text-purple-600 font-bold">Realtime Webhook</span>
            </div>
          </div>
        </div>

        {/* Narrator Typography Sitting in Natural Right Negative Space */}
        <div className="relative z-10 max-w-xl text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#2563EB]/10 border border-[#2563EB]/20 text-xs font-mono text-[#2563EB] font-semibold mb-4">
            <Smartphone className="w-3.5 h-3.5" />
            <span>STUDIO 02 // MOBILE APP ECOSYSTEMS</span>
          </div>

          <h3 className="text-3xl sm:text-5xl font-extrabold text-[#111318] tracking-tight leading-tight mb-5">
            YOUR BUSINESS. IN YOUR CUSTOMER&apos;S HANDS.
          </h3>

          <p className="text-base text-[#374151] leading-relaxed mb-6">
            From consumer-facing on-demand mobile apps to field logistics and offline-first employee tools. Native iOS (Swift), Android (Kotlin), and unified cross-platform solutions that sync seamlessly with your central database.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-8">
            {[
              "Native iOS & Android Architecture",
              "Offline-First SQLite Cache Sync",
              "Payment Gateways (Razorpay/Stripe)",
              "Instant WhatsApp Push Alerts",
              "Camera & Hardware Barcode Scanning",
              "App Store & Play Store Submissions",
            ].map((feat) => (
              <div key={feat} className="flex items-center gap-2 text-xs font-medium text-[#111318]">
                <CheckCircle2 className="w-4 h-4 text-[#2563EB] shrink-0" />
                <span>{feat}</span>
              </div>
            ))}
          </div>

          <div>
            <button
              onClick={() => onOpenDiscovery("mobile")}
              className="px-8 py-3.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white text-sm font-semibold transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] flex items-center gap-2"
            >
              <span>BUILD MY MOBILE APP</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Floating Studio Floor Marker */}
        <div className="absolute top-8 left-8 z-10 hidden sm:flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 border border-[#111318]/10 shadow-md text-[11px] font-mono text-[#111318]">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
          <span>LEVEL 2 // STUDIO 02 // MOBILE ECOSYSTEMS</span>
        </div>
      </section>

      {/* =========================================================================
          SCENE 07: CRM & OPERATIONS STUDIO — FULL-BLEED REAL OPERATIONS CANVAS
      ========================================================================= */}
      <section
        id="scene-studio-crm"
        className="relative min-h-screen w-full flex flex-col justify-center py-24 px-6 sm:px-8 lg:px-16 overflow-hidden border-b border-[#111318]/10 bg-slate-950 text-[#111318]"
      >
        {/* Real Operations Studio Photograph Canvas */}
        <div className="absolute inset-0 z-0 opacity-40">
          <Image
            src="/images/storytelling/office/crm.webp"
            alt="Johnson Softwares Operations and Pipeline Engineering Room"
            fill
            sizes="100vw"
            quality={80}
            loading="lazy"
            className="object-cover object-center"
          />
        </div>

        {/* Clean solid overlay without GPU-taxing backdrop-blur */}
        <div className="absolute inset-0 z-[1] bg-[#F8F8F6]/92 pointer-events-none" />

        <div className="relative z-10 w-full max-w-7xl mx-auto">
          {/* Narrator Headline */}
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-[#2563EB]/10 border border-[#2563EB]/20 text-xs font-mono text-[#2563EB] font-semibold mb-3">
              <Database className="w-3.5 h-3.5" />
              <span>STUDIO 03 // OPERATIONS &amp; PIPELINE ENGINE</span>
            </div>

            <h3 className="text-3xl sm:text-5xl md:text-6xl font-extrabold text-[#111318] tracking-tight leading-tight mb-5">
              YOUR BUSINESS DOESN&apos;T NEED ANOTHER TOOL.
              <br />
              <span className="text-[#2563EB]">IT NEEDS A SYSTEM.</span>
            </h3>

            <p className="text-base sm:text-lg text-[#5E636B] leading-relaxed">
              Stop paying monthly per-user licensing taxes for generic CRMs that require 4 plugins just to send a WhatsApp message. We engineer custom operational backbones with automated lead qualification.
            </p>
          </div>

          {/* Clean Information Pipeline Architecture */}
          <div className="mb-12 p-8 rounded-3xl bg-white/95 border border-[#111318]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="text-xs font-mono text-[#5E636B] uppercase tracking-wider text-center mb-8 font-semibold">
              THE AUTONOMOUS REVENUE PIPELINE
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
              <div className="p-4 rounded-xl bg-[#F8F8F6] border border-[#111318]/10 text-center shadow-xs">
                <Zap className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <div className="text-[10px] font-mono text-[#5E636B]">STAGE 01</div>
                <div className="text-sm font-bold text-[#111318]">NEW INBOUND LEAD</div>
                <div className="text-[11px] text-[#5E636B] mt-1">Ads / Web / Direct QR</div>
              </div>

              <div className="hidden md:flex justify-center text-[#111318]/30 font-bold">→</div>

              <div className="p-4 rounded-xl bg-blue-50/70 border border-[#2563EB]/30 text-center shadow-xs">
                <Bot className="w-6 h-6 text-[#2563EB] mx-auto mb-2" />
                <div className="text-[10px] font-mono text-[#2563EB] font-bold">STAGE 02</div>
                <div className="text-sm font-bold text-[#111318]">AI AGENT TRIAGE</div>
                <div className="text-[11px] text-[#5E636B] mt-1">&lt; 30-sec qualification</div>
              </div>

              <div className="hidden md:flex justify-center text-[#111318]/30 font-bold">→</div>

              <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-500/30 text-center shadow-xs">
                <MessageSquare className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <div className="text-[10px] font-mono text-emerald-600 font-bold">STAGE 03</div>
                <div className="text-sm font-bold text-[#111318]">WHATSAPP SYNC</div>
                <div className="text-[11px] text-[#5E636B] mt-1">Interactive instant chat</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div className="p-4 rounded-xl bg-[#F8F8F6] border border-[#111318]/10 text-center shadow-xs">
                <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <div className="text-[10px] font-mono text-[#5E636B]">STAGE 04</div>
                <div className="text-sm font-bold text-[#111318]">KANBAN DISPATCH</div>
                <div className="text-[11px] text-[#5E636B] mt-1">Routed to available closer</div>
              </div>

              <div className="p-4 rounded-xl bg-[#F8F8F6] border border-[#111318]/10 text-center shadow-xs">
                <BarChart3 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                <div className="text-[10px] font-mono text-[#5E636B]">STAGE 05</div>
                <div className="text-sm font-bold text-[#111318]">AUTOMATED FOLLOW-UP</div>
                <div className="text-[11px] text-[#5E636B] mt-1">0 manual reminders needed</div>
              </div>

              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-500/30 text-center shadow-xs">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                <div className="text-[10px] font-mono text-emerald-700 font-bold">STAGE 06</div>
                <div className="text-sm font-bold text-[#111318]">REVENUE WON</div>
                <div className="text-[11px] text-emerald-800 mt-1">Logged into accounting ledger</div>
              </div>
            </div>
          </div>

          {/* Embedded Real Working Interactive Simulator as Product Evidence */}
          <div className="mb-10">
            <div className="text-center mb-6">
              <span className="text-xs font-mono tracking-widest text-[#2563EB] uppercase font-semibold">
                INTERACTIVE DEMO // TEST THE REAL PIPELINE
              </span>
              <p className="text-xs text-[#5E636B] mt-1">
                Trigger a mock lead below to watch the event flow across webhook, qualification, and WhatsApp routing.
              </p>
            </div>
            <CrmAutomationSimulator />
          </div>

          {/* CRM Action Trigger */}
          <div className="text-center">
            <button
              onClick={() => onOpenDiscovery("crm")}
              className="px-8 py-4 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-sm transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] inline-flex items-center gap-2"
            >
              <span>ENGINEER MY CRM SYSTEM</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
